
import {
  Component, Ref, Prop, PropSync, Mixins,
} from 'vue-property-decorator';
import { VForm } from '@/features/common/types';
import {
  AvailableService,
  InProgress,
  PaymentInfoFormData,
  PaymentInfoRequest,
  PaymentInfoResponse,
  ShipFrom,
} from '@/features/shipment/types';
import {
  addPaymentInformation, getPaymentInformation,
} from '@/features/shipment/services/shipment.services';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { parseFirstAndLastName, showSuccessAlertDialog } from '@/features/common/helpers';
import Inputs from '@/features/shipment/components/checkout/Inputs.vue';
import Verification from '@/features/shipment/components/checkout/Verification.vue';
import { UserInfo } from '@/features/users/types';
import { CheckoutCreateLabelMixin } from './mixins';

type InputsRef = InstanceType<typeof Inputs>;

@Component({
  components: {
    Verification,
    Inputs,
  },
})
export default class CheckoutForm extends Mixins(CheckoutCreateLabelMixin) {
  @Ref('checkoutForm') form!: VForm;

  @Ref('inputs') inputs!: InputsRef;

  @Prop({ required: true }) isVerificationRequired!: boolean | null;

  @PropSync('existingCardInfo', { required: true }) existingCardInfoSynced!: PaymentInfoResponse | null;

  @PropSync('isCardInputsVisible', {
    required: false,
    default: true,
  }) isCardInputsVisibleSynced!: boolean;

  @Prop({ required: true }) useSecureship!: boolean;

  @Prop({ required: false, default: false }) onlyAddCard!: boolean;

  private isVerificationPageVisible = false;

  private isLoading = false;

  private isFormSubmitted = false;

  private cardData: PaymentInfoFormData = {
    cardNumber: '',
    month: '',
    year: '',
    cvv: '',
    contact: '',
    address: {
      addr1: '',
      addr2: '',
      countryCode: '',
      postalCode: '',
      city: '',
      province: '',
    },
    useShippingAddress: false,
  }

  get isDateValid(): boolean {
    const { month, year } = this.cardData;

    if (!month) return false;
    if (!year) return false;
    if (year.length < 2) return false;

    return true;
  }

  get paymentInfoForRequest(): PaymentInfoRequest {
    const {
      contact, cardNumber, cvv, address, month, year, useShippingAddress,
    } = this.cardData;

    const { firstName, lastName } = parseFirstAndLastName(contact);

    const fullYear = `${new Date().getFullYear().toString().slice(0, 2)}${year}`;

    const generateBillingAddress = (shippingAddress: ShipFrom) => ({
      addr1: shippingAddress.addr1,
      countryCode: shippingAddress.countryCode,
      postalCode: shippingAddress.postalCode,
      city: shippingAddress.city,
      province: shippingAddress.province,
    });

    const billingAddress = useShippingAddress && !this.onlyAddCard
      ? generateBillingAddress(this.inProgress.fromAddress) : address;

    const finalData: PaymentInfoRequest = {
      firstname: firstName,
      lastname: lastName,
      userAgent: navigator.userAgent,
      month,
      year: fullYear,
      cardNumber,
      cvv,
      address: billingAddress,
    };

    return finalData;
  }

  get service(): AvailableService {
    return this.$store.state.shipment.selectedService;
  }

  get inProgress(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get showAccountNumber(): boolean {
    if (this.onlyAddCard) return false;

    if (this.inProgress.billing.account) {
      return true;
    }

    if (!this.service) return false;

    return !this.useSecureship;
  }

  get accountNumberWithCarrier(): string {
    if (!this.showAccountNumber) return '';
    const accountNum = this.inProgress.billing.account || this.service.usersCarrierAccountNo;
    return `${this.service.carrierCode} - ${accountNum}`;
  }

  get isVerificationPage(): boolean {
    if (this.isVerificationPageVisible) return true;
    if (this.$route.name === 'shipment-verify') return true;
    return false;
  }

  get userInfo(): UserInfo {
    return this.$store.getters.getUserInfo;
  }

  mounted(): void {
    this.$nextTick(() => {
      this.isVerificationPageVisible = this.$route.name === 'shipment-verify';
    });
    eventBus.$on(eventNames.HIDE_VERIFICATION_PAGE, () => {
      this.isVerificationPageVisible = false;
    });

    this.check3dsError();
  }

  private check3dsError(): void {
    const { query } = this.$route;
    if (query.from === '3ds' && query.errorMessage) {
      const errorMessage = decodeURIComponent(query.errorMessage as string);

      this.showOopsDialog(errorMessage, false);

      this.$router.replace({
        name: 'shipment-checkout',
        query: {},
      });
    }
  }

  private selectExistingCard() {
    this.isCardInputsVisibleSynced = false;
  }

  private async submit() {
    if (this.isLoading) return;

    if (this.isCardInputsVisibleSynced && this.inputs?.paymentAddressInput
    && this.inputs.paymentAddressInput.ensureAddressDetailsVisible) {
      await this.inputs.paymentAddressInput.ensureAddressDetailsVisible();
    }

    this.isFormSubmitted = true;
    await this.$nextTick();

    const isValid = this.form.validate();
    if (!isValid) return;
    if (!this.isDateValid && this.isCardInputsVisibleSynced) return;

    this.isLoading = true;

    if (this.isCardInputsVisibleSynced) {
      const savedResponse = await addPaymentInformation(this.paymentInfoForRequest);

      if (!savedResponse || !savedResponse.success) {
        this.showOopsDialog(savedResponse?.error, false);
        this.isLoading = false;
        return;
      }

      await this.getAndSetExistingCard();
      this.resetForm();
      this.selectExistingCard();
    }

    if (this.onlyAddCard) {
      showSuccessAlertDialog(this.$t('paymentMethods.alerts.cardAdded') as string);
      this.isLoading = false;
      this.isFormSubmitted = false;
      return;
    }

    if (this.isVerificationRequired) {
      this.$router.replace({ name: 'shipment-verify' });
      this.isVerificationPageVisible = true;
      this.isLoading = false;
      this.isFormSubmitted = false;
      return;
    }

    await this.createLabelAndPrint();

    if (this.labelResponse?.errorType === 'CreditCardChallengeRequired' && this.labelResponse?.threeDsChallenge) {
      return;
    }

    this.isLoading = false;
    this.isFormSubmitted = false;
  }

  private async getAndSetExistingCard() {
    const paymentData = await getPaymentInformation();
    if (!paymentData) return;

    this.existingCardInfoSynced = paymentData;
  }

  private resetForm() {
    this.cardData = {
      cardNumber: '',
      month: '',
      year: '',
      cvv: '',
      contact: '',
      address: {
        addr1: '',
        addr2: '',
        countryCode: '',
        postalCode: '',
        city: '',
        province: '',
      },
      useShippingAddress: false,
    };

    if (this.$refs.checkoutForm) {
      (this.$refs.checkoutForm as HTMLFormElement).resetValidation();
    }
  }
}
